import React, { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';
import QuantityInput from './QuantityInput';
import ImageCustomizer from './ImageCustomizer';
import StepNavigation from './StepNavigation';
import axios from 'axios';
import Masonry from 'react-masonry-css';
import { getImageOrientation } from '../utils/productUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function SelectSection({ images, printSizes, onAddToCart, activeTab, setActiveTab, cartItems, setImages, onEditImage }) {
  const [selectedImages, setSelectedImages] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // 'all', 'selected', or null
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [addedCount, setAddedCount] = useState(0);
  const [bulkDiscounts, setBulkDiscounts] = useState({});
  const [sortBy, setSortBy] = useState('upload'); // 'upload', 'filename-asc', 'filename-desc'
  
  // Sort images based on selected sort option
  const sortedImages = useMemo(() => {
    if (!images || images.length === 0) return [];
    
    const sorted = [...images];
    switch (sortBy) {
      case 'filename-asc':
        sorted.sort((a, b) => (a.filename || '').localeCompare(b.filename || ''));
        break;
      case 'filename-desc':
        sorted.sort((a, b) => (b.filename || '').localeCompare(a.filename || ''));
        break;
      case 'upload':
      default:
        // Keep original upload order (no sorting needed)
        break;
    }
    return sorted;
  }, [images, sortBy]);
  
  // Get default size from printSizes - prefer featured product
  const getDefaultSize = React.useCallback(() => {
    if (!printSizes || Object.keys(printSizes).length === 0) return null;
    const featuredProduct = Object.entries(printSizes).find(([key, product]) => product.featured);
    return featuredProduct ? featuredProduct[0] : Object.keys(printSizes)[0];
  }, [printSizes]);
  
  // Initialize bulk settings with dynamic default
  const [bulkSettings, setBulkSettings] = useState({
    quantity: 1,
    size: null
  });
  
  // Update size when printSizes loads (only once)
  const sizeInitialized = React.useRef(false);
  React.useEffect(() => {
    if (!sizeInitialized.current && printSizes && Object.keys(printSizes).length > 0) {
      const defaultSku = getDefaultSize();
      if (defaultSku && bulkSettings.size !== defaultSku) {
        console.log('📦 SelectSection: Setting default size to', defaultSku);
        setBulkSettings(prev => ({ ...prev, size: defaultSku }));
        sizeInitialized.current = true;
      }
    }
  }, [printSizes, getDefaultSize, bulkSettings.size]);
  
  // Fetch bulk discounts
  useEffect(() => {
    const fetchBulkDiscounts = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/bulk-discounts`);
        const discountMap = {};
        response.data.discounts.forEach(d => {
          discountMap[d.product_sku] = {
            discount_price: d.discount_price,
            discount_quantity: d.discount_quantity
          };
        });
        setBulkDiscounts(discountMap);
        console.log('📊 Bulk discounts loaded in SelectSection:', discountMap);
      } catch (error) {
        console.error('Failed to load bulk discounts:', error);
      }
    };
    fetchBulkDiscounts();
  }, []);

  const toggleImageSelection = (imageIndex) => {
    setSelectedImages(prev => {
      const isSelected = prev.includes(imageIndex);
      if (isSelected) {
        return prev.filter(index => index !== imageIndex);
      } else {
        return [...prev, imageIndex];
      }
    });
  };

  const handleDeleteImage = (imageIndex) => {
    setImages(prev => prev.filter((_, index) => index !== imageIndex));
    setSelectedImages(prev => prev.filter(index => index !== imageIndex));
    toast.success('Image deleted successfully');
  };

  const handleDeleteAll = () => {
    setImages([]);
    setSelectedImages([]);
    setShowDeleteConfirm(null);
    toast.success('All images deleted successfully');
  };

  const handleDeleteSelected = () => {
    if (selectedImages.length === 0) {
      toast.error('No images selected');
      return;
    }
    setImages(prev => prev.filter((_, index) => !selectedImages.includes(index)));
    setSelectedImages([]);
    setShowDeleteConfirm(null);
    toast.success(`${selectedImages.length} images deleted successfully`);
  };

  const applyToSelected = async () => {
    if (selectedImages.length === 0) {
      toast.error('No images selected');
      return;
    }

    // Get saved crop data from localStorage
    const savedCropData = JSON.parse(localStorage.getItem('tempCropData') || '{}');
    const selectedSize = bulkSettings.size;
    
    // Get product info for this SKU
    const product = printSizes[selectedSize];

    // Process images sequentially to handle async orientation detection
    for (const imageIndex of selectedImages) {
      const image = images[imageIndex];
      if (image) {
        // Always use filename as the key for consistency
        const imageKey = image.filename;
        const imageCrops = savedCropData[imageKey] || {};
        
        console.log(`🔍 Looking up crops for ${imageKey}:`, Object.keys(imageCrops));
        
        // Default orientation based on image dimensions
        const imageData = image.image_data || image.preview;
        let aspectOrientation = 'landscape';
        let zoom = 1;
        
        const portraitKey = `${selectedSize}-portrait`;
        const landscapeKey = `${selectedSize}-landscape`;
        
        // Check for saved crop (user's explicit choice takes priority)
        if (imageCrops[portraitKey]) {
          const saved = imageCrops[portraitKey];
          aspectOrientation = 'portrait';
          zoom = saved.zoom || 1;
          console.log(`✅ Found portrait crop for ${selectedSize}: zoom=${zoom}`);
        } else if (imageCrops[landscapeKey]) {
          const saved = imageCrops[landscapeKey];
          aspectOrientation = 'landscape';
          zoom = saved.zoom || 1;
          console.log(`✅ Found landscape crop for ${selectedSize}: zoom=${zoom}`);
        } else if (imageCrops[selectedSize]) {
          // Fallback to old format without orientation suffix
          const saved = imageCrops[selectedSize];
          aspectOrientation = saved.aspectOrientation || 'landscape';
          zoom = saved.zoom || 1;
          console.log(`✅ Found legacy crop for ${selectedSize}: orientation=${aspectOrientation}, zoom=${zoom}`);
        } else if (imageData) {
          // No saved crop - detect orientation from image dimensions
          aspectOrientation = await getImageOrientation(imageData);
          console.log(`📐 Auto-detected orientation for ${imageKey}: ${aspectOrientation}`);
        } else {
          console.log(`⚠️ No saved crop or image data for ${imageKey} - ${selectedSize}, defaulting to landscape`);
        }
        
        // Default crop settings - will be recalculated by thumbnail generator
        const cropSettings = { x: 0, y: 0, width: 100, height: 100 };
        
        console.log(`📦 Apply to Selected: ${image.filename} - sku: ${selectedSize}, size: ${product?.size}, orientation: ${aspectOrientation}, zoom: ${zoom}x`);
        
        onAddToCart(image, {
          sku: selectedSize,  // SKU is the primary key
          size: product?.size || selectedSize,  // Display size like "6x4"
          quantity: bulkSettings.quantity,
          zoom: zoom,
          aspectOrientation: aspectOrientation,
          crop_settings: cropSettings,
          forceUpdate: true  // Always update existing cart items with new crop settings
        });
      }
    }

    setAddedCount(selectedImages.length);
    setShowSuccessDialog(true);
    setSelectedImages([]);
  };

  const applyToAll = async () => {
    // Get saved crop data from localStorage
    const savedCropData = JSON.parse(localStorage.getItem('tempCropData') || '{}');
    const selectedSize = bulkSettings.size;
    
    // Get product info for this SKU
    const product = printSizes[selectedSize];
    
    // Process images sequentially to handle async orientation detection
    for (const image of images) {
      // Always use filename as the key for consistency
      const imageKey = image.filename;
      const imageCrops = savedCropData[imageKey] || {};
      
      console.log(`🔍 Looking up crops for ${imageKey}:`, Object.keys(imageCrops));
      
      // Default orientation based on image dimensions
      const imageData = image.image_data || image.preview;
      let aspectOrientation = 'landscape';
      let zoom = 1;
      
      const portraitKey = `${selectedSize}-portrait`;
      const landscapeKey = `${selectedSize}-landscape`;
      
      // Check for saved crop (user's explicit choice takes priority)
      if (imageCrops[portraitKey]) {
        const saved = imageCrops[portraitKey];
        aspectOrientation = 'portrait';
        zoom = saved.zoom || 1;
        console.log(`✅ Found portrait crop for ${selectedSize}: zoom=${zoom}`);
      } else if (imageCrops[landscapeKey]) {
        const saved = imageCrops[landscapeKey];
        aspectOrientation = 'landscape';
        zoom = saved.zoom || 1;
        console.log(`✅ Found landscape crop for ${selectedSize}: zoom=${zoom}`);
      } else if (imageCrops[selectedSize]) {
        // Fallback to old format without orientation suffix
        const saved = imageCrops[selectedSize];
        aspectOrientation = saved.aspectOrientation || 'landscape';
        zoom = saved.zoom || 1;
        console.log(`✅ Found legacy crop for ${selectedSize}: orientation=${aspectOrientation}, zoom=${zoom}`);
      } else if (imageData) {
        // No saved crop - detect orientation from image dimensions
        aspectOrientation = await getImageOrientation(imageData);
        console.log(`📐 Auto-detected orientation for ${imageKey}: ${aspectOrientation}`);
      } else {
        console.log(`⚠️ No saved crop or image data for ${imageKey} - ${selectedSize}, defaulting to landscape`);
      }
      
      // Default crop settings
      const cropSettings = { x: 0, y: 0, width: 100, height: 100 };
      
      console.log(`📦 Apply to All: ${image.filename} - sku: ${selectedSize}, size: ${product?.size}, orientation: ${aspectOrientation}, zoom: ${zoom}x`);
      
      onAddToCart(image, {
        sku: selectedSize,  // SKU is the primary key
        size: product?.size || selectedSize,  // Display size like "6x4"
        quantity: bulkSettings.quantity,
        zoom: zoom,
        aspectOrientation: aspectOrientation,
        crop_settings: cropSettings,
        forceUpdate: true  // Always update existing cart items with new crop settings
      });
    }

    setAddedCount(images.length);
    setShowSuccessDialog(true);
  };

  return (
    <div className="space-y-6">
      <StepNavigation 
        currentStep="select"
        onStepChange={setActiveTab}
        canGoNext={cartItems.length > 0}
        canGoBack={true}
      />
      
      <div className="text-center">
        <div className="flex items-center justify-between mb-4">
          <div className="flex-1"></div>
          <div className="flex-1 text-center">
            <h2 className="text-3xl font-bold text-gray-900">Select Your Prints</h2>
          </div>
          <div className="flex-1"></div>
        </div>
        <p className="text-gray-600">Configure size, paper type, and quantity for your images</p>
      </div>

      {images.length === 0 ? (
        <div className="text-center py-12">
          <i className="pi pi-images text-6xl text-gray-400 mx-auto mb-4 block"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No images uploaded</h3>
          <p className="text-gray-600 mb-4">Upload some images first to start customising your prints</p>
          <Button 
            onClick={() => setActiveTab('upload')}
            className="bg-custom-orange hover:bg-custom-orange"
            data-testid="go-to-upload"
          >
            Go to Upload Images
          </Button>
        </div>
      ) : (
        <>
          {/* Bulk Print Settings */}
          <Card className="p-6 mb-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  {selectedImages.length > 0 && `${selectedImages.length} selected`}
                </span>
              </div>

              {/* Print Settings - Modern Style */}
              <div className="space-y-3">
                {/* Print Size Selection - Modern Cards */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Print Size</label>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
                  {Object.entries(printSizes).map(([key, value]) => {
                    const isSelected = bulkSettings.size === key;
                    const meetsThreshold = bulkDiscounts[value.sku] && bulkSettings.quantity >= bulkDiscounts[value.sku].discount_quantity;
                    
                    return (
                      <label 
                        key={key}
                        className={`animated-border-element flex flex-col cursor-pointer p-2 rounded-lg border-2 transition-all ${
                          isSelected 
                            ? 'border-custom-orange bg-orange-50' 
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        onClick={(e) => {
                          // Prevent triggering when clicking quantity input
                          if (!e.target.closest('.quantity-input-wrapper')) {
                            setBulkSettings(prev => ({ ...prev, size: key }));
                          }
                        }}
                      >
                        <div className="flex items-start justify-between gap-3">
                          {/* Left side: Radio + Name + Price + Discount */}
                          <div className="flex items-start gap-2 flex-1 min-w-0">
                            <div className="relative flex items-center mt-0.5">
                              <input
                                type="radio"
                                name="bulk-size"
                                value={key}
                                checked={isSelected}
                                onChange={() => setBulkSettings(prev => ({ ...prev, size: key }))}
                                className="sr-only"
                              />
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                isSelected 
                                  ? 'border-custom-orange bg-custom-orange' 
                                  : 'border-gray-300 bg-white'
                              }`}>
                                {isSelected && (
                                  <div className="w-2 h-2 rounded-full bg-white"></div>
                                )}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className={`text-sm font-semibold block ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                                {value.name}
                              </span>
                              <div className="text-xs text-gray-900 mt-0.5">
                                ${(value.price / 100).toFixed(2)} each
                              </div>
                              {bulkDiscounts[value.sku] && (
                                <div className="mt-0.5">
                                  {meetsThreshold ? (
                                    <div className="text-xs font-semibold text-green-600 flex items-center gap-1">
                                      <i className="pi pi-check-circle text-green-600"></i>
                                      ${(bulkDiscounts[value.sku].discount_price / 100).toFixed(2)} each
                                    </div>
                                  ) : (
                                    <div className="text-xs text-orange-600">
                                      ${(bulkDiscounts[value.sku].discount_price / 100).toFixed(2)} when qty ≥ {bulkDiscounts[value.sku].discount_quantity}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Right side: Quantity (hidden on mobile, shown on large screens) */}
                          {isSelected && (
                            <div className="hidden lg:block quantity-input-wrapper">
                              <QuantityInput
                                value={bulkSettings.quantity}
                                onChange={(newQty) => setBulkSettings(prev => ({ ...prev, quantity: newQty }))}
                                min={1}
                                max={999}
                              />
                            </div>
                          )}
                        </div>
                        
                        {/* Quantity below for mobile */}
                        {isSelected && (
                          <div className="lg:hidden flex items-center gap-2 mt-2 pt-2 border-t border-gray-200 quantity-input-wrapper">
                            <span className="text-sm text-gray-600">Qty:</span>
                            <QuantityInput
                              value={bulkSettings.quantity}
                              onChange={(newQty) => setBulkSettings(prev => ({ ...prev, quantity: newQty }))}
                              min={1}
                              max={999}
                            />
                          </div>
                        )}
                      </label>
                    );
                  })}
                  </div>
                </div>
              </div>

              {/* Batch Update Actions */}
              <div className="space-y-4">
                {/* Action Buttons - Delete buttons first, then Apply buttons */}
                <div className="flex flex-wrap gap-3 pt-2">
                  {/* Delete Buttons First */}
                  {/* Delete Buttons - Outline style with red color */}
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteConfirm('selected')}
                    disabled={selectedImages.length === 0}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="delete-selected"
                  >
                    <i className="pi pi-trash mr-2"></i>
                    Delete Selected ({selectedImages.length})
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteConfirm('all')}
                    disabled={selectedImages.length > 0}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="delete-all"
                  >
                    <i className="pi pi-trash mr-2"></i>
                    Delete All ({images.length})
                  </Button>
                  
                  {/* Apply Buttons - Outline style with orange color */}
                  <Button
                    variant="outline"
                    onClick={applyToSelected}
                    disabled={selectedImages.length === 0}
                    className="text-custom-orange hover:text-orange-700 hover:bg-orange-50"
                    data-testid="apply-to-selected"
                  >
                    Apply to Selected ({selectedImages.length})
                  </Button>
                  <Button
                    variant="outline"
                    onClick={applyToAll}
                    disabled={selectedImages.length > 0}
                    className="text-custom-orange hover:text-orange-700 hover:bg-orange-50"
                    data-testid="apply-to-all"
                  >
                    Apply to All ({images.length})
                  </Button>
                  
                  {/* More Images Button - Outline style with green color */}
                  <Button
                    variant="outline"
                    onClick={() => setActiveTab('upload')}
                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                    data-testid="more-images-button"
                  >
                    <i className="pi pi-plus mr-2"></i>
                    More Images
                  </Button>
                  
                  {/* Edit Images Button - Outline style with blue color */}
                  <Button
                    variant="outline"
                    onClick={() => {
                      // Open crop modal for first image (not selected images)
                      const imageToEdit = images[0];
                      
                      if (imageToEdit && onEditImage) {
                        const imageIndex = images.indexOf(imageToEdit);
                        onEditImage({
                          image: imageToEdit,
                          imageIndex: imageIndex,
                          currentSettings: {
                            size: bulkSettings.size,
                            paperType: 'matte',
                            border: false,
                            quantity: bulkSettings.quantity,
                            rotation: 0,
                            scale: 100,
                            cropSettings: { x: 0, y: 0, width: 100, height: 100 }
                          }
                        });
                      }
                    }}
                    disabled={images.length === 0 || selectedImages.length > 0}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    title={selectedImages.length > 0 ? "Deselect images to use Edit mode" : "Open crop editor to customize images"}
                    data-testid="edit-images-button"
                  >
                    <i className="pi pi-pencil mr-2"></i>
                    Edit Images
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Delete Confirmation Dialog */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowDeleteConfirm(null)}>
              <Card className="p-6 max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Deletion</h3>
                <p className="text-gray-600 mb-6">
                  {showDeleteConfirm === 'all' 
                    ? `Are you sure you want to delete all ${images.length} images? This action cannot be undone.`
                    : `Are you sure you want to delete ${selectedImages.length} selected images? This action cannot be undone.`
                  }
                </p>
                <div className="flex justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteConfirm(null)}
                    data-testid="cancel-delete"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={showDeleteConfirm === 'all' ? handleDeleteAll : handleDeleteSelected}
                    data-testid="confirm-delete"
                  >
                    Delete
                  </Button>
                </div>
              </Card>
            </div>
          )}

          {/* Success Dialog */}
          {showSuccessDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowSuccessDialog(false)}>
              <Card className="p-6 max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Added to Cart!</h3>
                  <p className="text-gray-600 mb-6">
                    Successfully added {addedCount} {addedCount === 1 ? 'image' : 'images'} to your cart.
                  </p>
                  <div className="flex justify-center space-x-3">
                    <Button
                      variant="outline"
                      onClick={() => setShowSuccessDialog(false)}
                      data-testid="continue-shopping"
                    >
                      Continue
                    </Button>
                    <Button
                      onClick={() => {
                        setShowSuccessDialog(false);
                        setActiveTab('cart');
                      }}
                      className="bg-custom-orange hover:bg-custom-orange"
                      data-testid="go-to-cart"
                    >
                      Go to Cart
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Sort Controls */}
          <div className="flex justify-between items-center mb-4">
            <span className="text-sm text-gray-600">{images.length} image{images.length !== 1 ? 's' : ''}</span>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-base border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-custom-orange focus:border-transparent min-w-[160px]"
                data-testid="sort-select"
              >
                <option value="upload">Upload Order</option>
                <option value="filename-asc">Filename (A-Z)</option>
                <option value="filename-desc">Filename (Z-A)</option>
              </select>
            </div>
          </div>

          {/* Pinterest-Style Masonry Grid */}
          <Masonry
            breakpointCols={{
              default: 4,
              1536: 4,
              1280: 3,
              1024: 3,
              768: 2,
              640: 1
            }}
            className="masonry-grid"
            columnClassName="masonry-grid-column"
          >
            {sortedImages.map((image, index) => {
              // Create a more specific key that changes when cart items or crops for this image change
              const imageCartItems = cartItems.filter(item => item.filename === image.filename);
              // Use SKU as the key component
              const cartKey = imageCartItems.map(item => `${item.sku || item.size}-${item.id}`).join('_');
              
              // Include crop data in key so grid re-renders when crops are added
              const cropsKey = image.croppedThumbnails 
                ? Object.keys(image.croppedThumbnails)
                    .map(sku => `${sku}-${image.croppedThumbnails[sku]?.aspectOrientation || 'landscape'}-${image.croppedThumbnails[sku]?.timestamp || ''}`)
                    .sort()
                    .join('_') 
                : 'nocrop';
              
              // Get original index from the images array (not sorted)
              const originalIndex = images.findIndex(img => img.filename === image.filename);
              
              return (
                <div key={`${image.filename}-${originalIndex}-${cartKey}-${cropsKey}`} className="masonry-grid-row">
                  <ImageCustomizer 
                    image={image}
                    imageIndex={originalIndex}
                    printSizes={printSizes}
                    onAddToCart={onAddToCart}
                    isSelected={selectedImages.includes(originalIndex)}
                    onToggleSelect={toggleImageSelection}
                    onDelete={handleDeleteImage}
                    onEdit={onEditImage}
                    cartItems={cartItems}
                    previewSize={bulkSettings.size}
                  />
                </div>
              );
            })}
          </Masonry>
        </>
      )}
    </div>
  );
}



export default SelectSection;
